import { useState } from 'react';
import { 
  CheckSquare, Square, X, Heart, Tag, 
  Folder, MoreHorizontal, ChevronDown 
} from 'lucide-react';
import { Button, Modal } from '../common';

import { useTagManagement } from '../../hooks/useTagManagement';
import { useReadingCollections } from '../../hooks/useReadingCollections';
import type { TarotReading } from '../../types/tarot';
import type { BulkOperation } from '../../types/collections';

interface BulkOperationsBarProps {
  readings: TarotReading[];
  filteredReadings: TarotReading[];
  selectedReadings: string[];
  isSelectionMode: boolean;
  onToggleSelection: (readingId: string) => void;
  onSelectAll: () => void;
  onSelectNone: () => void;
  onSelectFiltered: () => void;
  onExitSelectionMode: () => void;
  onExecuteBulkOperation: (operation: BulkOperation) => Promise<void>;
}

export default function BulkOperationsBar({
  readings,
  filteredReadings,
  selectedReadings,
  isSelectionMode,
  onToggleSelection: _onToggleSelection,
  onSelectAll: _onSelectAll,
  onSelectNone,
  onSelectFiltered,
  onExitSelectionMode,
  onExecuteBulkOperation
}: BulkOperationsBarProps) {
  const { tags } = useTagManagement(readings);
  const { collections } = useReadingCollections();
  
  const [showTagMenu, setShowTagMenu] = useState(false);
  const [showCollectionMenu, setShowCollectionMenu] = useState(false);
  const [showMoreMenu, setShowMoreMenu] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [isExecuting, setIsExecuting] = useState(false);

  const selectedCount = selectedReadings.length;
  const allSelected = filteredReadings.length > 0 && 
    filteredReadings.every(reading => selectedReadings.includes(reading.id));
  const someSelected = selectedReadings.length > 0 && !allSelected;

  const handleSelectToggle = () => {
    if (allSelected) {
      onSelectNone();
    } else {
      onSelectFiltered();
    }
  };

  const executeBulkOperation = async (operation: BulkOperation) => {
    setIsExecuting(true);
    try {
      await onExecuteBulkOperation(operation);
      setShowTagMenu(false);
      setShowCollectionMenu(false);
      setShowMoreMenu(false);
    } catch (error) {
      console.error('Bulk operation failed:', error);
    } finally {
      setIsExecuting(false);
    }
  };

  const handleAddTag = async (tagName: string) => {
    await executeBulkOperation({
      type: 'addTag',
      readingIds: selectedReadings,
      data: { tag: tagName }
    });
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handleRemoveTag = async (tagName: string) => {
    await executeBulkOperation({
      type: 'removeTag',
      readingIds: selectedReadings,
      data: { tag: tagName }
    });
  };

  const handleAddToCollection = async (collectionId: string) => {
    await executeBulkOperation({
      type: 'addToCollection',
      readingIds: selectedReadings,
      data: { collectionId }
    });
  };

  const handleFavorite = async () => {
    await executeBulkOperation({
      type: 'favorite',
      readingIds: selectedReadings
    });
  };

  const handleUnfavorite = async () => {
    await executeBulkOperation({
      type: 'unfavorite',
      readingIds: selectedReadings
    });
  };

  const handleDelete = async () => {
    await executeBulkOperation({
      type: 'delete',
      readingIds: selectedReadings
    });
    setConfirmDelete(false);
  };

  if (!isSelectionMode) {
    return null;
  }

  return (
    <>
      <div className="sticky top-0 z-10 bg-dark-900/95 backdrop-blur-sm border-b border-dark-700 p-4">
        <div className="flex items-center justify-between">
          {/* Selection Controls */}
          <div className="flex items-center gap-4">
            <button
              onClick={handleSelectToggle}
              className="flex items-center gap-2 text-sm text-gray-300 hover:text-white transition-colors"
            >
              {allSelected ? (
                <CheckSquare className="h-4 w-4 text-primary-500" />
              ) : someSelected ? (
                <div className="h-4 w-4 border border-primary-500 bg-primary-500/50 rounded flex items-center justify-center">
                  <div className="h-2 w-2 bg-primary-500 rounded-sm" />
                </div>
              ) : (
                <Square className="h-4 w-4" />
              )}
              {allSelected ? 'Deselect All' : 'Select All'}
            </button>

            <div className="text-sm text-gray-400">
              {selectedCount} of {filteredReadings.length} selected
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex items-center gap-2">
            {selectedCount > 0 && (
              <>
                {/* Favorite/Unfavorite */}
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={handleFavorite}
                  disabled={isExecuting}
                  className="text-gray-300 hover:text-red-400"
                >
                  <Heart className="h-4 w-4 mr-1" />
                  Favorite
                </Button>

                {/* Tags Menu */}
                <div className="relative">
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => setShowTagMenu(!showTagMenu)}
                    disabled={isExecuting}
                    className="text-gray-300 hover:text-primary-400"
                  >
                    <Tag className="h-4 w-4 mr-1" />
                    Tags
                    <ChevronDown className="h-3 w-3 ml-1" />
                  </Button>

                  {showTagMenu && (
                    <div className="absolute top-full right-0 mt-1 bg-dark-800 border border-dark-600 rounded-lg shadow-lg z-20 min-w-[200px]">
                      <div className="p-2 border-b border-dark-600">
                        <div className="text-xs font-medium text-gray-400 uppercase tracking-wide">
                          Add Tags
                        </div>
                      </div>
                      <div className="max-h-48 overflow-y-auto">
                        {tags.slice(0, 10).map(tag => (
                          <button
                            key={tag.id}
                            onClick={() => handleAddTag(tag.name)}
                            className="w-full px-3 py-2 text-left text-sm text-gray-300 hover:bg-dark-700 flex items-center gap-2"
                          >
                            <div
                              className="w-3 h-3 rounded-full"
                              style={{ backgroundColor: tag.color }}
                            />
                            {tag.name}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                {/* Collections Menu */}
                <div className="relative">
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => setShowCollectionMenu(!showCollectionMenu)}
                    disabled={isExecuting}
                    className="text-gray-300 hover:text-purple-400"
                  >
                    <Folder className="h-4 w-4 mr-1" />
                    Collections
                    <ChevronDown className="h-3 w-3 ml-1" />
                  </Button>

                  {showCollectionMenu && (
                    <div className="absolute top-full right-0 mt-1 bg-dark-800 border border-dark-600 rounded-lg shadow-lg z-20 min-w-[200px]">
                      <div className="p-2 border-b border-dark-600">
                        <div className="text-xs font-medium text-gray-400 uppercase tracking-wide">
                          Add to Collection
                        </div>
                      </div>
                      <div className="max-h-48 overflow-y-auto">
                        {collections.map(collection => (
                          <button
                            key={collection.id}
                            onClick={() => handleAddToCollection(collection.id)}
                            className="w-full px-3 py-2 text-left text-sm text-gray-300 hover:bg-dark-700 flex items-center gap-2"
                          >
                            <Folder 
                              className="h-3 w-3" 
                              style={{ color: collection.color }}
                            />
                            {collection.name}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                {/* More Actions */}
                <div className="relative">
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => setShowMoreMenu(!showMoreMenu)}
                    disabled={isExecuting}
                    className="text-gray-300"
                  >
                    <MoreHorizontal className="h-4 w-4" />
                  </Button>

                  {showMoreMenu && (
                    <div className="absolute top-full right-0 mt-1 bg-dark-800 border border-dark-600 rounded-lg shadow-lg z-20 min-w-[160px]">
                      <button
                        onClick={handleUnfavorite}
                        className="w-full px-3 py-2 text-left text-sm text-gray-300 hover:bg-dark-700"
                      >
                        Remove Favorites
                      </button>
                      <button
                        onClick={() => setConfirmDelete(true)}
                        className="w-full px-3 py-2 text-left text-sm text-red-400 hover:bg-dark-700"
                      >
                        Delete Selected
                      </button>
                    </div>
                  )}
                </div>
              </>
            )}

            {/* Exit Selection Mode */}
            <Button
              size="sm"
              variant="ghost"
              onClick={onExitSelectionMode}
              className="text-gray-400 hover:text-white"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {confirmDelete && (
        <Modal
          isOpen={true}
          onClose={() => setConfirmDelete(false)}
          title="Delete Selected Readings"
        >
          <div className="space-y-4">
            <p className="text-gray-300">
              Are you sure you want to delete {selectedCount} selected reading{selectedCount !== 1 ? 's' : ''}? 
              This action cannot be undone.
            </p>

            <div className="flex justify-end gap-3">
              <Button
                variant="secondary"
                onClick={() => setConfirmDelete(false)}
              >
                Cancel
              </Button>
              <Button
                onClick={handleDelete}
                disabled={isExecuting}
                className="bg-red-600 hover:bg-red-700"
              >
                {isExecuting ? 'Deleting...' : `Delete ${selectedCount} Reading${selectedCount !== 1 ? 's' : ''}`}
              </Button>
            </div>
          </div>
        </Modal>
      )}

      {/* Click outside to close menus */}
      {(showTagMenu || showCollectionMenu || showMoreMenu) && (
        <div 
          className="fixed inset-0 z-10"
          onClick={() => {
            setShowTagMenu(false);
            setShowCollectionMenu(false);
            setShowMoreMenu(false);
          }}
        />
      )}
    </>
  );
}